"use client";

import { useState, type ReactNode } from "react";
import { SupportTicketForm } from "./SupportTicketForm";
import type { PickerConfig, SupportPrefill } from "./types";

interface SupportTicketButtonProps {
  prefill: SupportPrefill;
  pickers?: PickerConfig[];
  endpoint?: string;
  /** Override the button content. Defaults to "?" floating / "Get help" inline. */
  buttonLabel?: ReactNode;
  /**
   * "floating" (default): fixed-position pill in the lower-right corner.
   * "inline": render as a normal inline button — caller controls placement.
   */
  mode?: "floating" | "inline";
  /** Override the trigger button's classes entirely (only used in inline mode). */
  triggerClassName?: string;
  /**
   * If set, clicking the button navigates to this URL instead of opening
   * the inline modal form. Used by apps with a richer dedicated /support/new
   * page (e.g. the therapist app's client/episode-aware form).
   */
  linkTo?: string;
}

/**
 * Drop-in help button + modal form. Default mode is "floating" — a "?" pill
 * fixed to the lower-right corner, mounted in each app's layout.
 * In "inline" mode the button is a normal inline element the caller places
 * wherever it makes sense (e.g. a header toolbar).
 */
export function SupportTicketButton({
  prefill,
  pickers,
  endpoint,
  buttonLabel,
  mode = "floating",
  triggerClassName,
  linkTo,
}: SupportTicketButtonProps) {
  const [open, setOpen] = useState(false);

  const floatingClass =
    "fixed bottom-5 right-5 z-[55] flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-lg font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:bg-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2";
  const defaultInlineClass =
    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all";

  const buttonClass =
    mode === "inline"
      ? (triggerClassName ?? defaultInlineClass)
      : floatingClass;

  const label =
    buttonLabel ?? (mode === "inline" ? "Get help" : "?");

  // Link mode: render as <a>, no modal at all.
  if (linkTo) {
    return (
      <a href={linkTo} aria-label="Get help" className={buttonClass}>
        {label}
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="Get help"
        onClick={() => setOpen(true)}
        className={buttonClass}
      >
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-900"
            >
              ✕
            </button>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Contact support
            </h2>
            <SupportTicketForm
              prefill={prefill}
              pickers={pickers}
              endpoint={endpoint}
              onSuccess={() => {
                // Stay open for ~3s so user sees the success state,
                // then close. They can dismiss earlier with ✕.
                setTimeout(() => setOpen(false), 4000);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
